/**
 * Layout helpers for Production Track lanes: sub-row packing without overlap.
 */

function overlaps(a, b) {
  const a0 = a.start_real_time || 0;
  const a1 = a.finish_real_time || a0;
  const b0 = b.start_real_time || 0;
  const b1 = b.finish_real_time || b0;
  return a0 < b1 && b0 < a1;
}

/**
 * Peak number of intervals active at any instant during [start, finish).
 */
export function peakConcurrencyDuringSpan(intervals, start, finish) {
  const events = [];
  for (const iv of intervals || []) {
    const s = Math.max(iv.start_real_time || 0, start);
    const f = Math.min(iv.finish_real_time || 0, finish);
    if (s < f) {
      events.push({ t: s, delta: 1 });
      events.push({ t: f, delta: -1 });
    }
  }
  // Process ends before starts at the same timestamp so touching intervals don't count as overlap.
  events.sort((a, b) => a.t - b.t || a.delta - b.delta);
  let cur = 0;
  let peak = 0;
  for (const e of events) {
    cur += e.delta;
    if (cur > peak) peak = cur;
  }
  return Math.max(1, peak);
}

/**
 * Greedy interval partitioning into sub-rows.
 * Height uses the max peak within each connected overlap cluster so mixed-peak
 * concurrent bars share one row stride and never visually collide.
 *
 * @returns {Array<{...interval, rowIndex: number, top: number, height: number}>}
 */
export function layoutLaneIntervals(intervals, laneHeight) {
  const list = intervals || [];
  if (list.length === 0) return [];

  const indexed = list.map((iv, i) => ({ iv, i }));
  indexed.sort(
    (a, b) =>
      (a.iv.start_real_time || 0) - (b.iv.start_real_time || 0) ||
      (a.iv.finish_real_time || 0) - (b.iv.finish_real_time || 0) ||
      a.i - b.i
  );

  const rowEnds = [];
  const rowIndexByOrig = new Array(list.length);

  for (const { iv, i } of indexed) {
    const start = iv.start_real_time || 0;
    const finish = iv.finish_real_time || start;
    let row = rowEnds.findIndex((end) => end <= start);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(finish);
    } else {
      rowEnds[row] = finish;
    }
    rowIndexByOrig[i] = row;
  }

  const parent = list.map((_, i) => i);
  const find = (i) => {
    let cur = i;
    while (parent[cur] !== cur) cur = parent[cur];
    let walk = i;
    while (parent[walk] !== walk) {
      const next = parent[walk];
      parent[walk] = cur;
      walk = next;
    }
    return cur;
  };
  const union = (i, j) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[rj] = ri;
  };

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (overlaps(list[i], list[j])) union(i, j);
    }
  }

  const peaks = list.map((iv) =>
    peakConcurrencyDuringSpan(list, iv.start_real_time || 0, iv.finish_real_time || 0)
  );
  const clusterPeak = {};
  for (let i = 0; i < list.length; i += 1) {
    const root = find(i);
    clusterPeak[root] = Math.max(clusterPeak[root] || 1, peaks[i]);
  }

  return list.map((iv, i) => {
    const peak = clusterPeak[find(i)] || 1;
    const height = laneHeight / peak;
    const rowIndex = rowIndexByOrig[i];
    return {
      ...iv,
      rowIndex,
      top: rowIndex * height,
      height,
    };
  });
}
